import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatIDR, formatDate } from "@/lib/format";
import { PageHeader, Loading, EmptyState, Field, ImageUpload } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Truck, CheckCheck, Search } from "lucide-react";
import { toast } from "sonner";

export default function Deliveries() {
  const { constants, selectedTripId } = useApp();
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 25;
  const [form, setForm] = useState({ order_id: "", recipient_name: "", phone: "", address: "", method: "Kurir Lokal", courier: "", tracking_number: "", delivery_fee: "0", proof_image: null, notes: "", status: "SIAP DIANTAR" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, o] = await Promise.all([
        api.get("/deliveries", { params: { trip_id: selectedTripId, search: search || undefined, page: 1, page_size: PAGE_SIZE } }),
        api.get("/orders", { params: { trip_id: selectedTripId } }),
      ]);
      setItems(d.data.items); setTotal(d.data.total); setHasMore(d.data.has_more); setPage(1);
      setOrders(o.data);
    } finally { setLoading(false); }
  }, [selectedTripId, search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = page + 1;
      const d = await api.get("/deliveries", { params: { trip_id: selectedTripId, search: search || undefined, page: next, page_size: PAGE_SIZE } });
      setItems((prev) => [...prev, ...d.data.items]); setHasMore(d.data.has_more); setPage(next);
    } finally { setLoadingMore(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm({ order_id: "", recipient_name: "", phone: "", address: "", method: "Kurir Lokal", courier: "", tracking_number: "", delivery_fee: "0", proof_image: null, notes: "", status: "SIAP DIANTAR" }); setOpen(true); };

  const save = async () => {
    if (!form.order_id) return toast.error("Pilih order");
    try { const res = await api.post("/deliveries", form); toast.success(res.data?.order_status === "SIAP DIANTAR" ? "Pengiriman disimpan \u00b7 Status order \u2192 SIAP DIANTAR" : "Pengiriman disimpan"); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const bulkDeliver = async () => {
    if (selected.length === 0) return toast.error("Pilih order dulu");
    try { await api.post("/deliveries/bulk-deliver", { order_ids: selected }); toast.success(`${selected.length} order ditandai terkirim`); setSelected([]); load(); }
    catch { toast.error("Gagal"); }
  };

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  return (
    <div>
      <PageHeader title="Pengiriman" subtitle="Kelola pengiriman order">
        {selected.length > 0 && <Button variant="outline" onClick={bulkDeliver} data-testid="bulk-deliver-button"><CheckCheck className="h-4 w-4 mr-1.5" />Tandai Terkirim ({selected.length})</Button>}
        <Button onClick={openNew} data-testid="delivery-create-button"><Plus className="h-4 w-4 mr-1.5" />Tambah Pengiriman</Button>
      </PageHeader>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Cari no. order, penerima, resi..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="deliveries-search" />
      </div>

      {loading ? <Loading /> : items.length === 0 ? (
        <EmptyState title={search ? "Tidak ada pengiriman yang cocok" : "Belum ada pengiriman"} action={<Button onClick={openNew}><Plus className="h-4 w-4 mr-1.5" />Tambah Pengiriman</Button>} />
      ) : (
        <div className="space-y-3">
          {items.map((d) => (
            <div key={d.id} className="rounded-xl border border-border bg-card p-4 shadow-sm flex items-center gap-3" data-testid="delivery-card">
              <input type="checkbox" checked={selected.includes(d.order_id)} onChange={() => toggle(d.order_id)} data-testid="delivery-select" />
              <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Truck className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{d.order_number} · {d.recipient_name}</p>
                <p className="text-xs text-muted-foreground">{d.method} {d.courier && `· ${d.courier}`} {d.tracking_number && `· ${d.tracking_number}`}</p>
              </div>
              <StatusBadge status={d.status} />
            </div>
          ))}
          {hasMore && (
            <div className="pt-1 flex justify-center">
              <Button variant="outline" onClick={loadMore} disabled={loadingMore} data-testid="deliveries-load-more">{loadingMore ? "Memuat…" : "Muat lebih banyak"}</Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground text-center" data-testid="deliveries-count">Menampilkan {items.length} dari {total} pengiriman</p>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Info Pengiriman</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Field label="Order" required>
              <SearchableSelect
                value={form.order_id}
                onChange={(v) => { const o = orders.find((x) => x.id === v); set("order_id", v); set("recipient_name", o?.customer_name || ""); set("phone", o?.customer_whatsapp || ""); }}
                options={orders.map((o) => ({ value: o.id, label: `${o.order_number} · ${o.customer_name}`, keywords: [o.order_number, o.customer_name, o.customer_whatsapp] }))}
                placeholder="Pilih order"
                searchPlaceholder="Cari no order / pelanggan..."
                emptyText="Order tidak ditemukan"
                testId="delivery-order-select"
              />
            </Field></div>
            <Field label="Nama Penerima"><Input value={form.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} /></Field>
            <Field label="Telepon"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            <div className="sm:col-span-2"><Field label="Alamat"><Textarea value={form.address} onChange={(e) => set("address", e.target.value)} /></Field></div>
            <Field label="Metode">
              <Select value={form.method} onValueChange={(v) => set("method", v)}><SelectTrigger data-testid="delivery-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(constants?.delivery_methods || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            </Field>
            <Field label="Kurir"><Input value={form.courier} onChange={(e) => set("courier", e.target.value)} /></Field>
            <Field label="No. Resi"><Input value={form.tracking_number} onChange={(e) => set("tracking_number", e.target.value)} /></Field>
            <Field label="Ongkir (Rp)"><Input type="number" value={form.delivery_fee} onChange={(e) => set("delivery_fee", e.target.value)} /></Field>
          </div>
          <ImageUpload value={form.proof_image} onChange={(b) => set("proof_image", b)} label="Bukti Kirim" testId="delivery-proof-upload" />
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="delivery-save-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
